import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { DialogRef } from '@ds/ng-wt-dialog';
import { Subscription, interval } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { idleTimeoutConfig } from 'src/app/const/constant';

export interface IdleWarningDialogData {
  onExtend: () => void;
  onLogout: () => void;
  onCountdownEnd: () => void;
}

/**
 * IdleTimeoutWarningDialogComponent
 * 
 * Non-dismissible warning dialog shown when user is about to be logged out due to inactivity
 * - 2-minute countdown timer
 * - "Extend Session" button to reset idle timer
 * - "Logout" button to immediately logout
 */
@Component({
  selector: 'ds-idle-timeout-warning-dialog',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './idle-timeout-warning-dialog.component.html',
  styleUrls: ['./idle-timeout-warning-dialog.component.scss'],
})
export class IdleTimeoutWarningDialogComponent implements OnInit, OnDestroy {
  countdownSeconds: number = idleTimeoutConfig.warningTime * 60; // 2 minutes
  isCountingDown: boolean = false;
  
  private countdownSubscription: Subscription | null = null;
  private countdownInterval: number = 1000;
  private globalClickListener: ((event: MouseEvent) => void) | null = null;
  private globalKeydownListener: ((event: KeyboardEvent) => void) | null = null;

  constructor(
    public dialogRef: DialogRef,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    console.log('[IdleWarningDialog] Dialog opened');
    
    // Disable body scroll
    document.body.style.overflow = 'hidden';
    
    this.startCountdown();
    this.setupEventBlockers();
  }

  private setupEventBlockers(): void {
    // Block Escape key
    this.globalKeydownListener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    
    // Block clicks outside buttons
    this.globalClickListener = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isButton = target.tagName === 'BUTTON' || target.closest('button');
      
      if (!isButton) {
        const dialogEl = document.querySelector('ds-idle-timeout-warning-dialog');
        if (dialogEl?.contains(target)) {
          // Click inside dialog but not on button - allow it
          return;
        }
        // Click outside dialog - block it
        event.preventDefault();
        event.stopPropagation();
      }
    };
    
    document.addEventListener('keydown', this.globalKeydownListener, true);
    document.addEventListener('click', this.globalClickListener, true);
  }

  private startCountdown(): void {
    this.isCountingDown = true;
    let remainingSeconds = this.countdownSeconds;

    this.countdownSubscription = interval(this.countdownInterval)
      .pipe(takeWhile(() => remainingSeconds > 0))
      .subscribe(() => {
        remainingSeconds--;
        this.countdownSeconds = remainingSeconds;
        this.cdr.markForCheck();

        if (remainingSeconds <= 0) {
          this.onCountdownEnd();
        }
      });
  }

  onExtendSession(): void {
    console.log('[IdleWarningDialog] Extended');
    this.stopCountdown();
    this.dialogRef.data?.onExtend?.();
    this.dialogRef.close();
  }

  onLogoutClick(): void {
    console.log('[IdleWarningDialog] Logout');
    this.stopCountdown();
    this.dialogRef.data?.onLogout?.();
    this.dialogRef.close();
  }

  private onCountdownEnd(): void {
    console.log('[IdleWarningDialog] Auto-logout');
    this.stopCountdown();
    this.dialogRef.data?.onCountdownEnd?.();
    this.dialogRef.close();
  }

  private stopCountdown(): void {
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
      this.countdownSubscription = null;
    }
    this.isCountingDown = false;
  }

  private removeEventBlockers(): void {
    if (this.globalClickListener) {
      document.removeEventListener('click', this.globalClickListener, true);
    }
    if (this.globalKeydownListener) {
      document.removeEventListener('keydown', this.globalKeydownListener, true);
    }
  }

  get formattedCountdown(): string {
    const minutes = Math.floor(this.countdownSeconds / 60);
    const seconds = this.countdownSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  ngOnDestroy(): void {
    this.stopCountdown();
    this.removeEventBlockers();
    document.body.style.overflow = '';
  }
}
